import { Link } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Panel } from '@/components/ui'
import { cn } from '@/lib/cn'

export interface GettingStartedPanelProps {
  companyId: string
  memberCount: number
  shiftsScheduledWeek: number
}

interface Step {
  key: string
  label: string
  hint: string
  to: string
  done: boolean
}

/**
 * Shown instead of the week chart while a company has no attendance history.
 *
 * An empty chart and a row of zeros read as "broken" on a new company. This
 * says what to do next instead, and disappears the moment real data exists.
 */
export function GettingStartedPanel({
  companyId,
  memberCount,
  shiftsScheduledWeek,
}: GettingStartedPanelProps): React.ReactElement {
  const steps: Step[] = [
    {
      key: 'members',
      label: 'Add members',
      hint: 'Import your team or add them one by one',
      to: `/companies/${companyId}/setup-wizard#import`,
      done: memberCount > 0,
    },
    {
      key: 'shifts',
      label: 'Schedule shifts',
      hint: 'Attendance is measured against a schedule',
      to: `/shifts?companyId=${companyId}`,
      done: shiftsScheduledWeek > 0,
    },
    {
      key: 'clock',
      label: 'Have the team clock in',
      hint: 'Metrics appear here once sessions start',
      to: `/attendance?companyId=${companyId}`,
      done: false,
    },
  ]

  const nextIndex = steps.findIndex((s) => !s.done)

  return (
    <Panel
      as="section"
      aria-labelledby="getting-started-heading"
      title={
        <div>
          <h3
            id="getting-started-heading"
            className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100"
          >
            Get this company running
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Attendance trends appear here once there is data to chart.
          </p>
        </div>
      }
      dense
      contentClassName="p-1.5"
    >
      <ol className="space-y-0.5">
        {steps.map((step, index) => {
          const isNext = index === nextIndex
          return (
            <li key={step.key}>
              <Link
                to={step.to}
                className={cn(
                  'group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-150',
                  'hover:bg-gray-50 dark:hover:bg-gray-700/40',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums',
                    step.done
                      ? 'border-teal-600 bg-teal-600 text-white dark:border-teal-500 dark:bg-teal-500'
                      : isNext
                        ? 'border-primary text-primary dark:border-sky-400 dark:text-sky-400'
                        : 'border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500'
                  )}
                  aria-hidden
                >
                  {step.done ? <Check className="h-3 w-3" strokeWidth={3} /> : index + 1}
                </span>

                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      step.done
                        ? 'text-gray-400 line-through dark:text-gray-500'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    {step.label}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {step.hint}
                  </span>
                </span>

                <span className="sr-only">{step.done ? '(done)' : '(not done)'}</span>
                <ArrowRight
                  className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-primary dark:text-gray-600"
                  aria-hidden
                />
              </Link>
            </li>
          )
        })}
      </ol>
    </Panel>
  )
}

GettingStartedPanel.displayName = 'GettingStartedPanel'
